// Note that this has some functional overlap with goals.ts, and the two should
// probably be merged eventually.

import * as yup from "yup";
import {
  isColumnReference,
  type TransformedColumn,
  type Column,
  type RawColumn,
  type RelatedColumn,
} from "./column";
import type { VisualQueryFilter } from "./filter";
import {
  AttributionBasis,
  AudienceAggregationType,
  PerUserAggregationType,
  PredefinedMetric,
  SyntheticMetricType,
  isModelMetricConfig,
  type EventFilter,
  type Goal,
  type SyntheticUserDefinedMetricConfig,
  type UserDefinedMetricConfig,
} from "./goals";
import {
  type MetricGroupByColumn,
  CohortType,
} from "@hightouch/core/server/graphql/types";

function userMetricDefinitionValidator(metricDefinition: Record<string, any>) {
  const config = metricDefinition.config;

  const eventConfigSchema = yup
    .object({
      eventModelId: yup.string().required(),
      relationshipId: yup.string().required(),
      // NOTE: the filter is complex to validate properly, as it may contain
      // nested conditions. For now just check that it's an array of objects.
      filter: yup.object({ subconditions: yup.array(yup.object()) }).required(),
    })
    .required();

  const modelConfigSchema = yup.object({
    modelId: yup.string().required(),
    relationshipId: yup.string().optional(),
    filter: yup.object({ subconditions: yup.array(yup.object()) }).required(),
  });

  return yup.object({
    parentModelId: yup.string().required(),
    aggregation: yup
      .string()
      .oneOf(Object.values(PerUserAggregationType))
      .required(),
    audienceAggregation: yup
      .string()
      .oneOf(Object.values(AudienceAggregationType))
      .required(),
    config: isModelMetricConfig(config) ? modelConfigSchema : eventConfigSchema,
    attributionWindow: yup
      .object({
        quantity: yup.number().integer().min(0).required(),
        unit: yup.string().oneOf(["day", "week", "month"]).required(),
        // Optional because there are existing metrics without a basis (defaults to "exit")
        basis: yup.string().oneOf(Object.values(AttributionBasis)),
      })
      .required(),
    cacheKey: yup.string().optional(),
  });
}

function decisionEngineMetricValidator() {
  return yup.object({
    parentModelId: yup.string().required(),
    aggregation: yup
      .string()
      .oneOf(Object.values(PerUserAggregationType))
      .required(),
    audienceAggregation: yup
      .string()
      .oneOf(Object.values(AudienceAggregationType))
      .required(),
    flowId: yup.string().required(),
    metricType: yup
      .string()
      .oneOf(Object.values(DecisionEngineAnalyticsCampaignMetricType))
      .required(),
    config: yup
      .object()
      .when("metricType", {
        is: DecisionEngineAnalyticsCampaignMetricType.Interactions,
        then: yup.object({
          source: yup
            .string()
            .oneOf(Object.values(SyntheticMetricType))
            .required(),
          resourceId: yup.string().required(),
          filter: yup
            .object({
              subconditions: yup.array(yup.object()),
            })
            .required(),
        }),
        otherwise: yup.object({
          eventModelId: yup.string().required(),
          relationshipId: yup.string().required(),
          filter: yup
            .object({
              subconditions: yup.array(yup.object()),
            })
            .required(),
        }),
      })
      .required(),
    cacheKey: yup.string().optional(),
  });
}

function predefinedMetricDefinitionValidator() {
  return yup.object({
    parentModelId: yup.string().required(),
    predefinedMetric: yup
      .string()
      .oneOf([PredefinedMetric.AudienceSize, PredefinedMetric.UserEvents])
      .required(),
    cacheKey: yup.string().optional(),
  });
}

export function getAnalyticsMetricDefinitionValidator() {
  return yup.lazy((metricDefinition: Record<string, any>) => {
    if (metricDefinition["predefinedMetric"]) {
      return predefinedMetricDefinitionValidator();
    }
    if (
      metricDefinition["type"] &&
      metricDefinition["type"] === "decision_engine_flow"
    ) {
      return decisionEngineMetricValidator();
    }
    return userMetricDefinitionValidator(metricDefinition);
  });
}

export function getAnalyticsCohortDefinitionValidator() {
  return yup.object({
    filter: yup
      .object({
        // XXX: note that this is a full audience filter condition, and will be
        // complicated to validate. We'll solve this eventually but for now just
        // validate that it's present.
        conditions: yup.array().required(),
        splitTestDefinition: yup
          .object({
            groupColumnName: yup.string().optional(),
            // XXX: didn't need the other fields for now so didn't include them,
            // but noting that here in case someone else comes along to use
            // this.
          })
          .optional(),
        subsetIds: yup.array().optional(),
      })
      .required(),
    parentModelId: yup.string().required(),
  });
}

export function getCohortInputValidator() {
  const analyticsCohortDefinitionValidator =
    getAnalyticsCohortDefinitionValidator();

  return yup
    .object({
      id: yup.string().optional(),
      cohortDefinition: yup.object().optional(),
      baselineGroup: yup.string().optional(),
      messageCampaignRunId: yup.string().uuid().optional(),
      type: yup.string().oneOf(Object.values(CohortType)).optional(),
    })
    .test(
      "exactly-one-cohort-identifier",
      "Each cohort must have exactly one of id or cohortDefinition, but not both",
      function (value) {
        const hasId = Boolean(value?.id);
        const hasCohortDefinition = Boolean(value?.cohortDefinition);

        // Exactly one should be true (XOR)
        return hasId !== hasCohortDefinition;
      },
    )
    .test(
      "baseline-group-only-with-id",
      "baselineGroup can only be provided when using an existing cohort id, not with ad-hoc cohortDefinition",
      function (value) {
        const hasBaselineGroup = Boolean(value?.baselineGroup);
        const hasCohortDefinition = Boolean(value?.cohortDefinition);

        // If baselineGroup is provided, cohortDefinition must not be provided
        return !hasBaselineGroup || !hasCohortDefinition;
      },
    )
    .test(
      "validate-cohort-definition",
      "Invalid cohort definition",
      async function (value) {
        if (value?.cohortDefinition) {
          try {
            await analyticsCohortDefinitionValidator.validate(
              value.cohortDefinition,
            );
            return true;
          } catch (error: any) {
            return this.createError({
              message: `Invalid cohort definition: ${error.message}`,
            });
          }
        }
        return true;
      },
    );
}

export function getAnalyticsFunnelStageValidator() {
  return yup.object({
    relationshipId: yup.string().required(),
    eventModelId: yup.string().required(),
    filter: yup
      .object({
        // XXX: note that this is an event filter condition, and will be
        // complicated to validate. We'll solve this eventually but for now just
        // validate that it's present.
        subconditions: yup.array().optional(),
      })
      .optional(),
  });
}

export enum AnalyticsFrequency {
  Daily = "daily",
  Weekly = "weekly",
  Monthly = "monthly",
  All = "all",
}

export enum GraphType {
  Line = "line",
  Column = "column",
  Bar = "bar",
  Table = "table",
  Area = "area",
  HeatMap = "heatmap",
  BigNumber = "big-number",
}

export type SavedChartType =
  | GraphType
  | "funnel"
  | "breakdown"
  | "insights"
  | "performance"
  | "splitsMeasurement";

export type ColumnWithAlias = {
  column: Column | SyntheticColumn;
  alias: string;
};

export type AnalyticsCohortDefinition = {
  parentModelId: string;
  filter: Pick<VisualQueryFilter, "conditions" | "splitTestDefinition">;
  name?: string;
  subsetIds?: string[];
  // Message cohorts only: which experiment. Every message experiment in a
  // workspace shares a parent model and an empty filter, so without this
  // nothing in the cohort tells two of them apart.
  messageExperimentId?: string;
  // Message cohorts only: scope the cohort to a single campaign run. Absent =
  // every send of the message.
  messageCampaignRunId?: string;
};

export type BaseAnalyticsMetricDefinition = {
  cacheKey?: string;
};

export type UserDefinedAnalyticsMetricDefinition =
  BaseAnalyticsMetricDefinition &
    Pick<
      Goal,
      | "parentModelId"
      | "aggregation"
      | "audienceAggregation"
      | "attributionWindow"
      | "config"
      | "attributionMethods"
      // Optionally pass in `id` to easily identify the metric from the client
    > & { config: UserDefinedMetricConfig; id?: string };

export type PredefinedAnalyticsMetricDefinition =
  BaseAnalyticsMetricDefinition & {
    parentModelId: string;
    predefinedMetric: string;
  };

export enum ExperimentsMetricType {
  SplitsMeasurement = "splits_measurement",
}

export type ExperimentsSplitsMeasurementMetricDefinition =
  UserDefinedAnalyticsMetricDefinition & {
    metricType: ExperimentsMetricType.SplitsMeasurement;
    config: UserDefinedMetricConfig & {
      normalization?: NormalizationType;
    };
  };

export type AnalyticsMetricDefinition =
  | UserDefinedAnalyticsMetricDefinition
  | PredefinedAnalyticsMetricDefinition
  | DecisionEngineAnalyticsMetricDefinition
  | ExperimentsSplitsMeasurementMetricDefinition;

export enum DecisionEngineAnalyticsCampaignMetricType {
  Interactions = "interactions",
  AttributedEvents = "attributed_events",
  Incrementality = "incrementality",
}

export enum MeasuringMode {
  Attribution = DecisionEngineAnalyticsCampaignMetricType.AttributedEvents,
  Incrementality = DecisionEngineAnalyticsCampaignMetricType.Incrementality,
  SplitsMeasurement = ExperimentsMetricType.SplitsMeasurement,
}

export enum NormalizationType {
  None = "none",
  RatePerUser = "rate_per_user",
  RatePerInteraction = "rate_per_interaction",

  // The ratio should be 1 if this is the treatment group, since it's already normalized.
  // Otherwise, it's the experiment size / treatment size
  // However, if we don't have any users in the experiment,
  // we should use 0 to prevent divide by 0 errors.
  TreatmentSize = "treatment_size",

  // This normalizes each group to 100% by dividing the value by the total count of users in the group.
  // For example, if there are 100 users total with 1 conversion each and 5 users in the treatment group,
  // the normalized value would be 100 / 5 * 5 = 100.
  // This tells us "if the treatment group was 100%, how many conversions would we have gotten".
  HundredPercent = "hundred_percent",

  // Incremental purchases that wouldn’t have happened without marketing,
  // which we can calculate by subtracting the treatment groups' values from the control groups' values.
  // So, for example, if the treatment is 100 purchases, and the holdout is 80 purchases,
  // the absolute lift is 20. We don't have a line for the treatment group, so we filter it out.
  AbsoluteLift = "absolute_lift",

  // Marketing drives up purchases by what %. Same as above, except we divide
  // the absolute lift by the size of the group (note, not the treatment group).
  LiftPercent = "lift_percent",
}

export type BaseDecisionEngineMetricDefinition =
  BaseAnalyticsMetricDefinition & {
    type: "decision_engine_flow";
    parentModelId: string;
    aggregation: PerUserAggregationType;
    audienceAggregation: AudienceAggregationType;
    flowId: string;
    // Optionally pass in `id` to easily identify the metric from the client
    id?: string;
  };

// Attribution metrics are metrics that have been attributed
// to a decision engine interaction through the AID attribution table, as
// determined through the flow's specific attribution logic (e.g. last touch within a 14 day window).
export type DecisionEngineAttributionMetricsDefinition =
  BaseDecisionEngineMetricDefinition & {
    metricType: DecisionEngineAnalyticsCampaignMetricType.AttributedEvents;
    config: {
      eventModelId: string;
      relationshipId: string;
      filter: EventFilter;
      normalization?: NormalizationType;
      column?: RawColumn | RelatedColumn;
    };
  };

// Interactions metrics are metrics that measure the base metric of the interactions
// for a given decision engine flow.
// These require their own metric type because the metric query cannot be generated from
// a model ID, as the interactions table is _not_ in the schema & is not a model.
// We provide the flow ID so we can generate the query from that flow's interactions table.
export type DecisionEngineInteractionsMetricDefinition =
  BaseDecisionEngineMetricDefinition & {
    metricType: DecisionEngineAnalyticsCampaignMetricType.Interactions;
    config: SyntheticUserDefinedMetricConfig;
  };

// Incrementality metrics measure conversions between
// two different experiment groups, so the events don't have to have been attributed
// to an interaction, it's just measuring which group the user belongs to.
export type DecisionEngineIncrementalityMetricsDefinition =
  BaseDecisionEngineMetricDefinition & {
    metricType: DecisionEngineAnalyticsCampaignMetricType.Incrementality;
    config: {
      eventModelId: string;
      relationshipId: string;
      filter: EventFilter;
      normalization?: NormalizationType;
      column?: RawColumn | RelatedColumn;
    };
  };

export type DecisionEngineAnalyticsMetricDefinition =
  | DecisionEngineAttributionMetricsDefinition
  | DecisionEngineInteractionsMetricDefinition
  | DecisionEngineIncrementalityMetricsDefinition;

export function isDecisionEngineAnalyticsMetricsDefinition(
  metric: AnalyticsMetricDefinition,
): metric is DecisionEngineAnalyticsMetricDefinition {
  return metric && "type" in metric && metric.type === "decision_engine_flow";
}

export function isDecisionEngineInteractionsMetricDefinition(
  metric: AnalyticsMetricDefinition,
): metric is DecisionEngineInteractionsMetricDefinition {
  return (
    isDecisionEngineAnalyticsMetricsDefinition(metric) &&
    metric.metricType === DecisionEngineAnalyticsCampaignMetricType.Interactions
  );
}

export function isDecisionEngineInteractionsMetricConfig(
  config:
    | DecisionEngineAnalyticsMetricDefinition["config"]
    | UserDefinedMetricConfig,
): config is DecisionEngineInteractionsMetricDefinition["config"] {
  return (
    "resourceId" in config &&
    config.source === SyntheticMetricType.DecisionEngineInteractions
  );
}

export function isDecisionEngineAnalyticsAttributionMetricsDefinition(
  metric: AnalyticsMetricDefinition,
): metric is DecisionEngineAttributionMetricsDefinition {
  return (
    isDecisionEngineAnalyticsMetricsDefinition(metric) &&
    metric.metricType ===
      DecisionEngineAnalyticsCampaignMetricType.AttributedEvents
  );
}

export function isDecisionEngineIncrementalityMetricDefinition(
  metric: AnalyticsMetricDefinition,
): metric is DecisionEngineIncrementalityMetricsDefinition {
  return (
    isDecisionEngineAnalyticsMetricsDefinition(metric) &&
    metric.metricType ===
      DecisionEngineAnalyticsCampaignMetricType.Incrementality
  );
}

export const TAG_FEATURE_PREFIX = "tag_feature_";
export const VARIABLE_TAG_FEATURE_PREFIX = "variable_tag_feature_";

export const variableTagColumnName = (variableName: string, tagKey: string) =>
  `${VARIABLE_TAG_FEATURE_PREFIX}${variableName.toLowerCase()}_${tagKey.toLowerCase()}`;

export enum DecisionEngineInteractionColumnNames {
  UserId = "primary_key",
  ReadyToSend = "ready_to_send",
  Experiment = "experiment",
}

export const SYNTHETIC_COLUMN_TYPES = [
  "decision_engine_interaction",
  "decision_engine_interaction_user_features",
  "decision_engine_interaction_action_features",
] as const;

export type DecisionEngineInteractionColumn = {
  type: (typeof SYNTHETIC_COLUMN_TYPES)[0];
  name: DecisionEngineInteractionColumnNames;
  alias?: string;
};
export type DecisionEngineInteractionUserFeaturesColumn = {
  type: (typeof SYNTHETIC_COLUMN_TYPES)[1];
  // User features are different for each decision engine flow,
  // so we can't enforce types here.
  name: string;
  alias?: string;
};

export type DecisionEngineInteractionActionFeaturesColumns = {
  type: (typeof SYNTHETIC_COLUMN_TYPES)[2];
  // Action features have some common names, but some are different for each
  // decision engine flow based on what AID is recommending.
  name: "channel" | "day_of_week" | "time_of_day" | "message" | string;
  alias?: string;
};

export const DEFAULT_DECISION_ENGINE_ACTION_FEATURES_COLUMNS: DecisionEngineInteractionActionFeaturesColumns[] =
  [
    {
      type: SYNTHETIC_COLUMN_TYPES[2],
      name: "message",
      alias: "Message",
    },
    {
      type: SYNTHETIC_COLUMN_TYPES[2],
      name: "day_of_week",
      alias: "Day of week",
    },
    {
      type: SYNTHETIC_COLUMN_TYPES[2],
      name: "time_of_day",
      alias: "Time of day",
    },
    {
      type: SYNTHETIC_COLUMN_TYPES[2],
      name: "channel",
      alias: "Channel",
    },
  ];

export type ModelNameColumn = {
  type: "model_name";
  name: "model_name";
  alias?: string;
};

export type SyntheticColumn =
  | DecisionEngineInteractionColumn
  | DecisionEngineInteractionUserFeaturesColumn
  | DecisionEngineInteractionActionFeaturesColumns
  | ModelNameColumn;

export type GroupByBucket = {
  start: number;
  end?: number;
};

export type GroupByColumn = (
  | RawColumn
  | RelatedColumn
  | SyntheticColumn
  | TransformedColumn
) & {
  buckets?: GroupByBucket[];
};

export function isSyntheticColumn(column: any): column is SyntheticColumn {
  return (
    isColumnReference(column) &&
    "type" in column &&
    [
      "decision_engine_interaction",
      "decision_engine_interaction_user_features",
      "decision_engine_interaction_action_features",
    ].includes(column.type)
  );
}

export function isUserDefinedAnalyticsMetricDefinition(
  metric: AnalyticsMetricDefinition,
): metric is UserDefinedAnalyticsMetricDefinition {
  return (
    metric &&
    "config" in metric &&
    typeof metric.config === "object" &&
    "attributionWindow" in metric
  );
}

export function isPredefinedAnalyticsMetricDefinition(
  metric: AnalyticsMetricDefinition,
): metric is PredefinedAnalyticsMetricDefinition {
  if (!metric) return false;
  return (
    "predefinedMetric" in metric && typeof metric.predefinedMetric === "string"
  );
}

export function isExperimentsSplitsMeasurementMetricDefinition(
  metric: AnalyticsMetricDefinition,
): metric is ExperimentsSplitsMeasurementMetricDefinition {
  return (
    metric != null &&
    (metric as any).metricType === ExperimentsMetricType.SplitsMeasurement
  );
}

export type AnalyticsFunnelStageDefinition = {
  relationshipId: string;
  eventModelId: string;
  filter: EventFilter;
};

export type DateOrString = Date | string;

export type AnalyticsMetricDatapoint<TTime extends DateOrString = Date> = {
  timestamp: TTime;
  value: number;
  // We optionally allow the point to contain any additional metadata
  // about the point. For example, we can return confidence intervals here
  // for rate based normalization metrics.
  // Other users are free to add their types here.
  meta?: NormalizedToRateMetadata | NormalizedToLiftMetadata;
};

export type SplitGroupId =
  | "holdout"
  | "customer_managed"
  | "unpersonalized"
  | "treatment";

export type NormalizedToRateMetadata = {
  lowerConfidenceBound: number;
  upperConfidenceBound: number;
  numerator: number;
  denominator: number;
  eventCount?: number;
  usersWithEvents?: number;
  variance?: number;
  totalSum?: number;
  sumOfSquares?: number;
  converterCount?: number;
};

export type WinProbability = {
  winPercent: number;
  winValueRemaining: number;
  lossPercent: number;
  lossValueRemaining: number;
};

export type NormalizedToLiftMetadata = {
  conversions: number;
  groupSize: number;
  winProbability: WinProbability;
  // null if there is no baseline group
  // in that case, values below will be 0
  baselineSplitId: SplitGroupId | null;
  baselineConversions: number;
  baselineGroupSize: number;
  baselineWinProbability: WinProbability;
  // Win probability from a pairwise comparison of the `treatment` and `customer_managed`
  // groups only. Populated on `treatment` and `customer_managed` data points when both
  // groups are present on the flight. Lets consumers surface a narrower statsig signal
  // when a small, noisy baseline (e.g. holdout) is diluting the full multi-way winPercent.
  treatmentVsCustomer?: {
    treatment: {
      // Treatment's conversions minus customer_managed's conversions normalized
      // to treatment's group size. Same formula as `normalizeToLift`, anchored
      // on `customer_managed` rather than the default baseline.
      // Both `lift` and `liftPercent` are populated regardless of the outer
      // normalization variant (AbsoluteLift vs LiftPercent).
      lift: number;
      liftPercent: number;
      winProbability: WinProbability;
      perConverterWinProbability?: WinProbability;
    };
    customerManaged: {
      winProbability: WinProbability;
      perConverterWinProbability?: WinProbability;
    };
  };
  totalSum?: number;
  sumOfSquares?: number;
  converterCount?: number;
  baselineTotalSum?: number;
  baselineSumOfSquares?: number;
  baselineConverterCount?: number;
  perConverterWinProbability?: WinProbability;
  baselinePerConverterWinProbability?: WinProbability;
};

export type AnalyticsGroupByKey = {
  column: Column | SyntheticColumn;
  value: string;
};

// XXX: Redis and SQS both serialize Dates as strings - this is a generic so we
// can change the date types depending on whether we're creating the types
// ourselves or reading from SQS/Redis, and make typescript tell us if we misuse
// the results.
export type AnalyticsMetricSeries<TTime extends DateOrString = Date> = {
  splitId?: string;
  groupBy: AnalyticsGroupByKey[];
  data: AnalyticsMetricDatapoint<TTime>[];
};

export type AnalyticsAudienceSizeBySplit = Record<
  string,
  {
    splitName: string | null;
    size: number;
  }
>;

export type AnalyticsAggregatedMetricValueBySplit = Record<
  string,
  {
    splitName: string | null;
    value: number;
  }
>;

export type AnalyticsMetricMetadataBySplit = Record<
  string,
  {
    eventCount?: number;
    usersWithEvents?: number;
    variance?: number;
  }
>;

export type AnalyticsMetricSummary = {
  timeWindow: {
    start: string; // ISO datetime string
    end: string; // ISO datetime string
  };
  data: AnalyticsSummaryStats[];
};

export type ConfidenceInterval = {
  lower: number;
  upper: number;
  confidence: number;
};

export type AnalyticsSummaryStats = {
  splitName: string | null;
  rawValue: number;
  value: number;
  size: number;
  isBaseline: boolean;
  isNormalized: boolean;
  confidenceInterval: ConfidenceInterval | null;
  lift: {
    // The difference in per user value between the treatment and the baseline
    // e.g. if the treatment is 200 and the baseline is 50, the lift is 3
    // (300% lift)
    value: number;
    confidenceInterval: ConfidenceInterval | null;
  } | null;
};

export type AnalyticsMetricFunnel = {
  // XXX: GraphQL (or at least Apollo) requires explicit nulls for optional
  // types, so just require it here to avoid the hastle of converting it later.
  splitId: string | null;
  groupBy: AnalyticsGroupByKey[];
  stages: {
    stage: AnalyticsFunnelStageDefinition;
    count: number;
  }[];
};

export type CohortFilter = Pick<VisualQueryFilter, "conditions">;

export const graphTypeRequiresAllFrequency = (
  graphType: GraphType | undefined,
): boolean => {
  return (
    graphType !== undefined &&
    [
      GraphType.Bar,
      GraphType.Table,
      GraphType.HeatMap,
      GraphType.BigNumber,
    ].includes(graphType)
  );
};

export const transformSavedChartFrequency = (
  frequency: string | undefined,
  graphType: GraphType | undefined,
): AnalyticsFrequency => {
  if (graphTypeRequiresAllFrequency(graphType)) {
    return AnalyticsFrequency.All;
  }

  // Make sure that we don't use `all` for line charts
  return frequency === "all"
    ? AnalyticsFrequency.Weekly
    : (frequency as AnalyticsFrequency);
};

// TODO: We shouldn't be saving placeholder columns to the database;
export const isGroupByColumnPlaceholder = (
  column: GroupByColumn | undefined,
): boolean => {
  return (
    column?.type === "raw" && column?.name === "" && column?.modelId === ""
  );
};

export const transformGroupByColumns = (
  groupByColumns: GroupByColumn[] | null,
): MetricGroupByColumn[] => {
  return (groupByColumns ?? [])
    .filter((column) => !isGroupByColumnPlaceholder(column))
    .map((groupBy: GroupByColumn) => {
      const { buckets, ...gbColumn } = groupBy;

      if (gbColumn?.type === "raw") {
        return {
          column: {
            ...gbColumn,
            modelId: gbColumn.modelId.toString(),
          },
          ...(buckets ? { buckets } : {}),
        };
      } else if (
        gbColumn?.type === "related" &&
        gbColumn.column?.type === "raw"
      ) {
        return {
          column: {
            ...gbColumn,
            column: {
              ...gbColumn.column,
              modelId: gbColumn.column.modelId.toString(),
            },
          },
          ...(buckets ? { buckets } : {}),
        };
      } else if (
        gbColumn?.type === "related" &&
        gbColumn.column?.type === "trait"
      ) {
        return {
          column: {
            ...gbColumn,
            column: {
              ...gbColumn.column,
              traitDefinitionId: gbColumn.column.traitDefinitionId,
            },
          },
          ...(buckets ? { buckets } : {}),
        };
      } else if (
        gbColumn?.type === "transformed" &&
        gbColumn.column?.type === "trait"
      ) {
        return {
          column: {
            ...gbColumn,
            column: {
              ...gbColumn.column,
              traitDefinitionId: gbColumn.column.traitDefinitionId,
            },
          },
          ...(buckets ? { buckets } : {}),
        };
      } else if (isSyntheticColumn(gbColumn)) {
        return {
          column: gbColumn,
          ...(buckets ? { buckets } : {}),
        };
      } else {
        throw new Error(
          `Unexpected group by column type: ${gbColumn.type}, ${gbColumn.column?.type}`,
        );
      }
    });
};
